// Custom hook for organization form state and logic
import { useState, useEffect, useCallback } from 'react';
import { IOrganization, ICreateOrganization, IEntrataUser } from '@/types/admin/organizations';
import { DEFAULT_ORGANIZATION_FORM_STATE, DEFAULT_ORGANIZATION_ERRORS } from '@/constants/admin/organizationFormDefaults';
import { 
  usePostOrganizationMutation, 
  usePutOrganizationMutation,
  useDeleteOrganizationMutation,
} from '@/store/api/admin/organizationsApi';
import { useLazyListConfigurationsQuery, usePatchConfigurationMutation } from '@/store/api/admin/configurationsApi';
import { useToast } from '@/contexts/admin/ToastContext';

interface UseOrganizationFormProps {
  organization?: IOrganization | null;
  onSuccess?: () => void;
  onClose?: () => void;
}

interface OrganizationFormData {
  name: string;
  domain: string;
  paymentAccounts: any[];
  entrataUsers: IEntrataUser[];
}

export const useOrganizationForm = ({ 
  organization, 
  onSuccess, 
  onClose 
}: UseOrganizationFormProps) => {
  const { showSuccess, showError } = useToast();
  const isEditMode = !!organization;

  // API mutations
  const [postOrganization, { isLoading: isCreating }] = usePostOrganizationMutation();
  const [putOrganization, { isLoading: isUpdating }] = usePutOrganizationMutation();
  const [deleteOrganization, { isLoading: isDeleting }] = useDeleteOrganizationMutation();
  const [listConfigurations, { data: configurationsData, isLoading: isLoadingConfigs }] = 
    useLazyListConfigurationsQuery();
  const [patchConfiguration, { isLoading: isPatchingConfig }] = usePatchConfigurationMutation();

  // Form state
  const [formData, setFormData] = useState<OrganizationFormData>(DEFAULT_ORGANIZATION_FORM_STATE);
  const [errors, setErrors] = useState(DEFAULT_ORGANIZATION_ERRORS);

  // Load Entrata configurations when in edit mode
  useEffect(() => {
    if (isEditMode && organization?.id) {
      listConfigurations(`?organizationId=${organization.id}&key=ENTRATA_ACCOUNTS`);
    }
  }, [isEditMode, organization?.id, listConfigurations]);

  // Update form data when organization or configurations change
  useEffect(() => {
    if (isEditMode && organization) {
      const entrataUsers = configurationsData?.response?.[0]?.value || [];
      setFormData({
        name: organization.name || '',
        domain: organization.domain || '',
        paymentAccounts: organization.paymentAccounts || DEFAULT_ORGANIZATION_FORM_STATE.paymentAccounts,
        entrataUsers: Array.isArray(entrataUsers) ? entrataUsers : [],
      });
    }
  }, [isEditMode, organization, configurationsData]);

  // Validation
  const validateForm = useCallback((): boolean => {
    const newErrors = {
      name: '',
      domain: '',
    };

    if (!formData.name.trim()) {
      newErrors.name = 'Organization name is required';
    }

    if (!formData.domain.trim()) {
      newErrors.domain = 'Domain is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.domain)) {
      newErrors.domain = 'Domain must contain only lowercase letters, numbers, and hyphens';
    }

    setErrors(newErrors);
    return !newErrors.name && !newErrors.domain;
  }, [formData]);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData(DEFAULT_ORGANIZATION_FORM_STATE);
    setErrors(DEFAULT_ORGANIZATION_ERRORS);
  }, []);

  // Update form field
  const updateField = useCallback(<K extends keyof OrganizationFormData>(
    field: K,
    value: OrganizationFormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Update payment account
  const updatePaymentAccount = useCallback((index: number, accountNumber: string) => {
    const newAccounts = [...formData.paymentAccounts];
    newAccounts[index] = {
      ...newAccounts[index],
      accountNumber: accountNumber || null,
    };
    setFormData(prev => ({ ...prev, paymentAccounts: newAccounts }));
  }, [formData.paymentAccounts]);

  // Save Entrata configuration (non-blocking helper)
  const saveEntrataConfig = useCallback(async (orgId: number, users: IEntrataUser[]) => {
    if (users.length === 0) return;

    try {
      await patchConfiguration({
        organizationId: orgId,
        propertyId: null,
        key: 'ENTRATA_ACCOUNTS',
        value: users,
      }).unwrap();
    } catch (configError: any) {
      console.warn('Failed to save Entrata users configuration:', configError);
      if (configError?.status === 'FETCH_ERROR' || configError?.message?.includes('CORS')) {
        console.error('CORS Error: Backend server needs to allow PATCH method for /api/v1/configurations/');
      }
    }
  }, [patchConfiguration]);

  // Create organization
  const createOrganization = useCallback(async () => {
    if (!validateForm()) return;

    try {
      const orgData: ICreateOrganization = {
        name: formData.name.trim(),
        domain: formData.domain.trim(),
        paymentAccounts: formData.paymentAccounts,
      };

      const result = await postOrganization(orgData).unwrap();
      const newOrgId = result?.response?.id;

      // Save Entrata users if configured
      if (newOrgId && formData.entrataUsers.length > 0) {
        await saveEntrataConfig(newOrgId, formData.entrataUsers);
      }

      showSuccess(`Organization "${formData.name}" created successfully`);
      onSuccess?.();
      onClose?.();
      resetForm();
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to create organization';
      showError(errorMessage);
    }
  }, [formData, validateForm, postOrganization, saveEntrataConfig, showSuccess, showError, onSuccess, onClose, resetForm]);

  // Update organization
  const updateOrganization = useCallback(async () => {
    if (!validateForm() || !organization) return;

    try {
      const updateData: IOrganization = {
        ...organization,
        name: formData.name.trim(),
        domain: formData.domain.trim(),
        paymentAccounts: formData.paymentAccounts,
      };

      await putOrganization(updateData).unwrap();
      
      // Save Entrata configuration
      await saveEntrataConfig(organization.id, formData.entrataUsers);

      showSuccess(`Organization "${formData.name}" updated successfully`);
      onSuccess?.();
      onClose?.();
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to update organization';
      showError(errorMessage);
    }
  }, [formData, validateForm, organization, putOrganization, saveEntrataConfig, showSuccess, showError, onSuccess, onClose]);

  // Delete organization
  const removeOrganization = useCallback(async () => {
    if (!organization) return;

    try {
      await deleteOrganization(organization.id).unwrap();
      showSuccess(`Organization "${organization.name}" deleted successfully`);
      onClose?.();
      onSuccess?.();
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to delete organization';
      showError(errorMessage);
    }
  }, [organization, deleteOrganization, showSuccess, showError, onClose, onSuccess]);

  return {
    // State
    formData,
    errors,
    isEditMode,
    isLoadingConfigs,
    
    // Loading states
    isLoading: isCreating || isUpdating || isPatchingConfig,
    isCreating,
    isUpdating,
    isDeleting,
    isPatchingConfig,
    
    // Actions
    updateField,
    updatePaymentAccount,
    validateForm,
    resetForm,
    createOrganization,
    updateOrganization,
    removeOrganization,
  };
};

